/**
 * Invitation API Functions
 *
 * API functions for company invitation operations
 */

import type {
  CompanyInvitation,
  CreateInvitationDto,
  InvitationStatus,
} from '@lognai/plugin-admin';
import { apiFetch, buildQueryString, type PaginatedListResponse } from './client';
import type { ListQueryParams } from '@lognai/frontend-core';

const BASE_URL = '/api/companies';

/**
 * Invitation with enriched data
 */
export interface EnrichedInvitation extends CompanyInvitation {
  role?: {
    id: string;
    name: string;
    code: string;
  };
  invitedByUser?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
}

/**
 * Invitation filter fields
 */
export interface InvitationFilter {
  status?: InvitationStatus;
}

/**
 * Invitation query params
 */
export type InvitationQueryParams = ListQueryParams<InvitationFilter>;

/**
 * Get list of invitations for a company
 */
export const listInvitations = async (
  companyId: string,
  params?: InvitationQueryParams
): Promise<PaginatedListResponse<EnrichedInvitation>> => {
  const query = buildQueryString(params as Record<string, unknown>);
  return apiFetch<PaginatedListResponse<EnrichedInvitation>>(
    `${BASE_URL}/${companyId}/invitations${query}`
  );
};

/**
 * Get a single invitation by ID
 */
export const getInvitation = async (
  companyId: string,
  invitationId: string
): Promise<EnrichedInvitation> => {
  return apiFetch<EnrichedInvitation>(
    `${BASE_URL}/${companyId}/invitations/${invitationId}`
  );
};

/**
 * Create an invitation
 */
export const createInvitation = async (
  companyId: string,
  dto: CreateInvitationDto
): Promise<EnrichedInvitation> => {
  return apiFetch<EnrichedInvitation>(`${BASE_URL}/${companyId}/invitations`, {
    method: 'POST',
    body: JSON.stringify(dto),
  });
};

/**
 * Cancel an invitation
 */
export const cancelInvitation = async (
  companyId: string,
  invitationId: string
): Promise<void> => {
  return apiFetch<void>(
    `${BASE_URL}/${companyId}/invitations/${invitationId}`,
    {
      method: 'DELETE',
    }
  );
};

/**
 * Anonymous preview of an invitation, as served by the public by-token
 * endpoint. `accountExists: false` means accepting requires a password
 * (the accept flow creates the account). `restoresAccount: true` means the
 * email's previously deleted account is reactivated under the new password.
 */
export interface InvitationPreview {
  email: string;
  status: string;
  expiresAt: string;
  message?: string;
  department?: string;
  jobTitle?: string;
  companyName?: string;
  roleName?: string;
  accountExists: boolean;
  restoresAccount?: boolean;
}

/**
 * Resolve an invitation by its token (public endpoint) so the accept page
 * can show company/role/inviter context before the invitee has a session.
 */
export const getInvitationByToken = async (
  token: string
): Promise<{ invitation: InvitationPreview }> => {
  return apiFetch<{ invitation: InvitationPreview }>(
    `${BASE_URL}/invitations/by-token?token=${encodeURIComponent(token)}`
  );
};

export interface AcceptInvitationInput {
  token: string;
  /** Required when no account exists for the invitation's email. */
  password?: string;
  fullName?: string;
}

/**
 * Accept an invitation (public endpoint).
 *
 * Path note: these routes mount under the admin plugin's /api/companies
 * prefix — the old `/api/invitations/...` string matched no route and every
 * accept 404'd.
 */
export const acceptInvitation = async (
  input: AcceptInvitationInput
): Promise<{ membership: EnrichedInvitation }> => {
  return apiFetch<{ membership: EnrichedInvitation }>(`${BASE_URL}/invitations/accept`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
};

/**
 * Decline an invitation (public endpoint — path note as acceptInvitation)
 */
export const declineInvitation = async (
  token: string,
  reason?: string
): Promise<void> => {
  return apiFetch<void>(`${BASE_URL}/invitations/decline`, {
    method: 'POST',
    body: JSON.stringify({ token, reason }),
  });
};
